import { useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { View, ScrollView } from "react-native";
import Animated, { FadeInRight, FadeOutLeft } from "react-native-reanimated";
import { useDispatch } from "react-redux";

import Title from "@/components/Title";
import TwoFactorAuth from "@/components/sections/userpanel/TwoFactorAuth";
import UpdateApplication from "@/components/sections/userpanel/UpdateApplication";
import Waitlist from "@/components/sections/userpanel/Waitlist";
import { setForm } from "@/context/actions/actions";
import { sendRequest } from "@/utils/ApiUtil";
import {
  getIsBridgeActiveFromCookies,
  setBridgeStatusInCookies,
} from "@/utils/cookies/Cookies";
import { getBridgeStatus } from "@/utils/web3/Utils";

export type Application = "CORPORATION" | "INDIVIDUAL";

export interface IndividualsRequirements {
  id: string;
  missingDocuments?: string[];
  missingFields: string[];
  firstName?: string;
  firstname?: string;
  countryCode?: string;
}

export interface DocumentRequirements {
  document: string;
  documentId?: string;
}

export interface BridgeRequirements {
  status: string;
  message: string;
  countryCode?: string;
  missingFields?: string[];
  missingDocuments?: DocumentRequirements[];
  missingIndividualDetails?: IndividualsRequirements[];
}

export default function UserPanel() {
  const [isBridgeApproved, setIsBridgeApproved] = useState(true);
  const [isBridgeWaitlisted, setIsBridgeWaitlisted] = useState(false);
  const [requirements, setRequirements] = useState<BridgeRequirements | null>(
    null
  );
  const [applicationId, setApplicationId] = useState("");
  const [applicationType, setApplicationType] =
    useState<Application>("CORPORATION");

  const dispatch = useDispatch();
  const { t } = useTranslation();

  useFocusEffect(
    React.useCallback(() => {
      dispatch(setForm({ currentMenuItem: "" }));
      const bridgeStatus = getIsBridgeActiveFromCookies();
      if (bridgeStatus !== "ACTIVE") getBridgeActivate();
      return () => {
        setIsBridgeWaitlisted(false);
      };
    }, [])
  );

  const fetchApplication = async () => {
    const request = await sendRequest("GET", "/applications", null, true);
    setApplicationId(request.application[0].id);
    setApplicationType(request.application[0].applicationType);
    return request.application[0];
  };

  const getBridgeActivate = async () => {
    const application = await fetchApplication();
    try {
      const request = await sendRequest(
        "get",
        "/bridge/activate",
        { applicationId: application.id },
        true
      );
      setRequirements(request);

      const status = request.status.toLowerCase();

      switch (status) {
        case "notauthorized":
          setIsBridgeWaitlisted(true);
          setIsBridgeApproved(false);
          break;
        case "incomplete":
          setIsBridgeApproved(false);
          break;
        case "created":
          setIsBridgeApproved(false);
          break;
        case "rejected":
          setIsBridgeApproved(false);
          setBridgeStatusInCookies("REJECTED");
          break;
        default:
          setIsBridgeApproved(true);
          await getBridgeStatus();
          break;
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  return (
    <ScrollView className="bg-white1 flex-grow px-10">
      <View className="flex-col w-full items-center justify-center p-4">
        <Title text={t("home/userpanel:title")} />
      </View>
      <Animated.View className="w-full gap-2">
        {!isBridgeApproved && !isBridgeWaitlisted && (
          <Animated.View entering={FadeInRight} exiting={FadeOutLeft}>
            <UpdateApplication
              requirements={
                requirements ?? {
                  status: "",
                  message: "",
                  missingFields: [],
                  missingDocuments: [],
                  missingIndividualDetails: [],
                }
              }
              applicationId={applicationId ?? ""}
              applicationType={applicationType}
              hasSubmitted={() => getBridgeActivate()}
            />
          </Animated.View>
        )}
        {isBridgeWaitlisted && (
          <Animated.View entering={FadeInRight} exiting={FadeOutLeft}>
            <Waitlist />
          </Animated.View>
        )}
        <TwoFactorAuth />
      </Animated.View>
    </ScrollView>
  );
}
